/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, SensorType} from '@ion/core/src/types/entities';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import NoSensorData from './NoSensorData';
import {SensorAccordion} from './SensorAccordion';
import ShockSensorEventStepper from './ShockSensorEventStepper';
import ShockSensorGraphic from './ShockSensorGraphic';
import ShockSensorStatus from './ShockSensorStatus';

export interface ShockSensorAccordionProps {
  title: string;
  data: GenericObject;
  loading: boolean;
  // true only for the Asset Details Drawer's own SC sensor graphs - passed
  // through to SensorAccordion to gate its current theme section card ...
  isAssetDetailsCard?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    container : {
      height        : 300,
      maxWidth      : 300,
      paddingBottom : '24px'
    },
    chartContainer : {
      height : 260,
      width  : 'inherit'
    },
    noData : {
      padding : '0 15px'
    },
    content : {
      display                        : 'flex',
      justifyContent                 : 'space-between',
      paddingLeft                    : '15px',
      height                         : 'auto',
      [theme.breakpoints.down('sm')] : {
        height : 'auto'
      }
    },
    // current theme drops the legacy side padding so the row aligns to the
    // 424px content box ...
    currentContent : {
      paddingLeft : 0
    },
    currentNoData : {
      padding : 0
    }
  };
});

/**
 * ShockSensorAccordion is a component used for displaying SC Device Shock sensor data.
 *
 * ```typescript
 *  import {ShockSensorAccordion} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 *   <ShockSensorAccordion data={shockData} />
 * ```
 */
export const ShockSensorAccordion = ({data, loading, isAssetDetailsCard}: ShockSensorAccordionProps) => {
  const classes                  = useStyles();
  const isCurrent                = isCurrentTheme();
  const shock                    = translate('tracking:scDeviceSensorTitle.shock');
  const shockData                = data?.data?.slice(0, 4);
  const {event, settings, value} = shockData?.[0] || {};
  const {maxImpact}              = settings || {};
  const {type}                   = event || {};
  const impactArea               = type;
  const isOverThreshold          = value > maxImpact;

  return (
    <SensorAccordion title={shock}
      isLoading={loading}
      isAssetDetailsCard={isAssetDetailsCard}>
      {!isEmpty(data?.data) ? (
        <>
          <ShockSensorStatus data={data} />
          <div className={themedClassName(classes.content, classes.currentContent, isCurrent)}>
            <ShockSensorEventStepper data={shockData} />
            <ShockSensorGraphic impactArea={impactArea} isOverThreshold={isOverThreshold} />
          </div>
        </>
      ) : (
        <div className={themedClassName(classes.noData, classes.currentNoData, isCurrent)}>
          {/* the current theme picks its shock icon by sensor type ... */}
          <NoSensorData sensorType={isCurrent ? SensorType.Shock : shock} iconSize={32} />
        </div>
      )}
    </SensorAccordion>
  );
};
